import { useEffect, useMemo, useState } from "react";
import type { RuntimeValue } from "../api/config";
import { useCopy } from "../i18n/copy";

const normalizeTimestamp = (value: unknown) => {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 0;
  return timestamp >= 100000000000
    ? Math.floor(timestamp / 1000)
    : Math.floor(timestamp);
};

const toSeconds = (value: RuntimeValue) => {
  const startTimestamp = normalizeTimestamp(value.startTimestamp);
  const serverTimestamp = normalizeTimestamp(value.serverTimestamp);
  if (startTimestamp > 0 && serverTimestamp >= startTimestamp) {
    return serverTimestamp - startTimestamp;
  }

  return Math.max(
    0,
    Math.floor(
      Number(value.day || 0) * 86400 +
        Number(value.hour || 0) * 3600 +
        Number(value.minute || 0) * 60 +
        Number(value.second || 0),
    ),
  );
};

const partsFromSeconds = (value: number) => {
  const seconds = Math.max(0, Math.floor(value));
  return {
    day: Math.floor(seconds / 86400),
    hour: Math.floor((seconds % 86400) / 3600),
    minute: Math.floor((seconds % 3600) / 60),
    second: seconds % 60,
  };
};

export function RuntimeCounter({ value }: { value: RuntimeValue }) {
  const { tr } = useCopy();
  const initial = useMemo(() => toSeconds(value), [value]);
  const [seconds, setSeconds] = useState(initial);

  useEffect(() => setSeconds(initial), [initial]);
  useEffect(() => {
    const timer = window.setInterval(
      () => setSeconds((current) => current + 1),
      1000,
    );
    return () => window.clearInterval(timer);
  }, []);

  const parts = partsFromSeconds(seconds);
  return (
    <section className="runtime-counter">
      <div className="runtime-primary">
        <small>{tr("Tiempo de operación")}</small>
        <strong>{parts.day}</strong>
        <span>{tr("días")}</span>
      </div>
      <div className="runtime-parts">
        <span>
          <strong>{String(parts.hour).padStart(2, "0")}</strong>
          <small>{tr("Horas")}</small>
        </span>
        <span>
          <strong>{String(parts.minute).padStart(2, "0")}</strong>
          <small>{tr("Minutos")}</small>
        </span>
        <span>
          <strong>{String(parts.second).padStart(2, "0")}</strong>
          <small>{tr("Segundos")}</small>
        </span>
      </div>
    </section>
  );
}
